import { useMemo } from 'react'
import { z } from 'zod'
import { zodValidator } from '@tanstack/zod-adapter'
import { subDays } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { PageHeader } from '@/components/shared/page-header'
import { SectionCard } from '@/components/shared/section-card'
import { RankingList } from '@/components/shared/ranking-list'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, TrendingUp, TrendingDown, ShoppingBag, Image } from 'lucide-react'
import { useCreatorDashboard, useCommissionTiers, useCreatorPrograms } from '@/hooks'
import {
  CreatorsAnalyticsFilters,
  DEFAULT_PERIOD,
  type PeriodValue,
} from '@/components/creators/creators-analytics-filters'
import type { KpiMetric, CreatorDashboardData } from '@/lib/api'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, BarChart, Bar, Legend } from 'recharts'

const PERIOD_DAYS: Record<PeriodValue, number | null> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  all: null,
}

const searchSchema = z.object({
  period: z.enum(['7d', '30d', '90d', 'all']).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  programId: z.string().optional(),
  tierId: z.string().optional(),
})

export const Route = createFileRoute('/_app/creators/analytics')({
  component: CreatorDashboardPage,
  validateSearch: zodValidator(searchSchema),
})

// ── Labels ──

const STATUS_LABELS: Record<string, string> = {
  prospecting: 'Prospectando',
  negotiating: 'Negociando',
  active: 'Ativo',
  paused: 'Pausado',
  churned: 'Churned',
}

const CONTRACT_LABELS: Record<string, string> = {
  commission: 'Comissao',
  barter: 'Permuta',
  fixed: 'Fixo',
  fixed_plus_commission: 'Fixo + Comissao',
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-primary',
  prospecting: 'bg-amber-500',
  negotiating: 'bg-blue-500',
  paused: 'bg-muted-foreground',
  churned: 'bg-destructive',
}

const CONTRACT_COLORS: Record<string, string> = {
  commission: 'bg-primary',
  barter: 'bg-amber-500',
  fixed: 'bg-blue-500',
  fixed_plus_commission: 'bg-purple-500',
}

// ── Chart Config ──

const CHART_COLOR_UP = 'var(--chart-1)' // emerald
const CHART_COLOR_DOWN = 'var(--destructive)'

const POST_TYPE_LABELS: Record<string, string> = {
  feed: 'Feed',
  reel: 'Reel',
  carousel: 'Carrossel',
  story: 'Story',
}

const POST_TYPE_CHART_COLORS: Record<string, string> = {
  feed: 'var(--chart-2)',
  reel: 'var(--chart-1)',
  carousel: 'var(--chart-3)',
  story: 'var(--chart-4)',
}

const postsChartConfig = {
  feed: { label: 'Feed', color: 'var(--chart-2)' },
  reel: { label: 'Reel', color: 'var(--chart-1)' },
  carousel: { label: 'Carrossel', color: 'var(--chart-3)' },
  story: { label: 'Story', color: 'var(--chart-4)' },
} satisfies ChartConfig

// ── KPI Config ──

type KpiCardConfig = {
  key: keyof CreatorDashboardData['kpis']
  title: string
  format: (value: number) => string
  invertTrend?: boolean
}

const KPI_CARDS: KpiCardConfig[] = [
  { key: 'activeCreators', title: 'Creators Ativos', format: (v) => v.toLocaleString('pt-BR') },
  { key: 'totalSales', title: 'Vendas no Periodo', format: fmtCurrency },
  { key: 'pendingCommission', title: 'Comissao Pendente', format: fmtCurrency, invertTrend: true },
  { key: 'paidCommission', title: 'Comissao Paga', format: fmtCurrency },
  { key: 'seedingsSent', title: 'Seedings Enviados', format: (v) => v.toLocaleString('pt-BR') },
  { key: 'seedingsCost', title: 'Custo de Seedings', format: fmtCurrency, invertTrend: true },
  { key: 'postsTracked', title: 'Posts Rastreados', format: (v) => v.toLocaleString('pt-BR') },
  { key: 'totalEngagement', title: 'Engajamento Total', format: fmtCompact },
]

// ── Helpers ──

function fmtCompact(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`
  return v.toLocaleString('pt-BR')
}

function fmtCurrency(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

// ── Skeletons ──

function KpiCardSkeleton() {
  return (
    <Card>
      <CardContent>
        <Skeleton className="h-4 w-20 mb-2" />
        <Skeleton className="h-8 w-24 mb-1" />
        <Skeleton className="h-3 w-16" />
      </CardContent>
    </Card>
  )
}

function ChartCardSkeleton({ className }: { className?: string }) {
  return (
    <Card className={className}>
      <CardHeader>
        <Skeleton className="h-5 w-48" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-48 w-full" />
      </CardContent>
    </Card>
  )
}

// ── Change Badge ──

function ChangeBadge({ change, invert }: { change: number; invert?: boolean }) {
  if (change === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-sm bg-muted text-muted-foreground">
        0%
      </span>
    )
  }
  const isPositive = change > 0
  const isGood = invert ? !isPositive : isPositive

  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-sm ${
        isGood ? 'bg-primary/10 text-primary' : 'bg-destructive/10 text-destructive'
      }`}
    >
      {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {isPositive ? '+' : ''}
      {change}%
    </span>
  )
}

// ── KPI Card ──

function KpiCard({ config, metric }: { config: KpiCardConfig; metric: KpiMetric }) {
  return (
    <Card className="transition-all duration-200 hover:shadow-raised hover:-translate-y-px">
      <CardContent>
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          {config.title}
        </p>
        <span className="text-2xl font-bold font-display">{config.format(metric.value)}</span>
        <div className="mt-2">
          <ChangeBadge change={metric.changePercent} invert={config.invertTrend} />
        </div>
      </CardContent>
    </Card>
  )
}

// ── Distribution Bar (replaces ugly pie/donut charts) ──

function DistributionList({
  data,
  labels,
  colors,
}: {
  data: Array<{ key: string; count: number }>
  labels: Record<string, string>
  colors: Record<string, string>
}) {
  const total = data.reduce((acc, d) => acc + d.count, 0)
  if (total === 0) {
    return <p className="text-sm text-muted-foreground text-center py-6">Nenhum dado</p>
  }

  return (
    <div className="space-y-3">
      {/* Stacked bar */}
      <div className="flex h-3 rounded-sm overflow-hidden gap-0.5">
        {data.map((d) => {
          const pct = (d.count / total) * 100
          if (pct === 0) return null
          return (
            <div
              key={d.key}
              className={`${colors[d.key] ?? 'bg-muted'} transition-all duration-300`}
              style={{ width: `${pct}%` }}
            />
          )
        })}
      </div>
      {/* Legend rows with individual progress bars */}
      <div className="space-y-3">
        {data.map((d) => {
          const pct = total > 0 ? Math.round((d.count / total) * 100) : 0
          return (
            <div key={d.key} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div
                    className={`h-2.5 w-2.5 rounded-sm shrink-0 ${colors[d.key] ?? 'bg-muted'}`}
                  />
                  <span>{labels[d.key] ?? d.key}</span>
                </div>
                <div className="flex items-center gap-2 tabular-nums text-muted-foreground">
                  <span className="font-medium text-foreground">{d.count}</span>
                  <span className="text-xs w-8 text-right">{pct}%</span>
                </div>
              </div>
              <div className="h-1.5 rounded-sm bg-muted overflow-hidden">
                <div
                  className={`h-full rounded-sm transition-all duration-500 ${colors[d.key] ?? 'bg-muted-foreground'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Main Page ──

function CreatorDashboardPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const selectedProgram = search.programId ?? 'all'
  const selectedTier = search.tierId ?? 'all'

  // Active period: from URL, or default if no custom dates
  const hasCustomDates = !!(search.startDate && search.endDate && !search.period)
  const activePeriod: PeriodValue | undefined = hasCustomDates
    ? undefined
    : (search.period ?? DEFAULT_PERIOD)

  // Compute effective dates from period or custom range
  const effectiveDates = useMemo(() => {
    if (hasCustomDates) {
      return { startDate: search.startDate, endDate: search.endDate }
    }
    const period = activePeriod ?? DEFAULT_PERIOD
    if (period === 'all') return {}
    const days = PERIOD_DAYS[period]!
    const end = new Date()
    const start = subDays(end, days - 1)
    return { startDate: start.toISOString(), endDate: end.toISOString() }
  }, [hasCustomDates, search.startDate, search.endDate, activePeriod])

  // Only show dates in picker when using custom range, not presets
  const dateRange: DateRange | undefined =
    hasCustomDates && effectiveDates.startDate && effectiveDates.endDate
      ? { from: new Date(effectiveDates.startDate), to: new Date(effectiveDates.endDate) }
      : undefined

  const filters = {
    startDate: effectiveDates.startDate,
    endDate: effectiveDates.endDate,
    programId: search.programId,
    tierId: search.tierId,
  }

  const handlePeriodChange = (period: PeriodValue) => {
    navigate({
      search: (prev) => ({
        programId: prev.programId,
        tierId: prev.tierId,
        period,
        startDate: undefined,
        endDate: undefined,
      }),
    })
  }
  const handleProgramChange = (val: string) => {
    navigate({ search: (prev) => ({ ...prev, programId: val === 'all' ? undefined : val }) })
  }
  const handleTierChange = (val: string) => {
    navigate({ search: (prev) => ({ ...prev, tierId: val === 'all' ? undefined : val }) })
  }
  const handleDateChange = (range: DateRange | undefined) => {
    navigate({
      search: (prev) => ({
        programId: prev.programId,
        tierId: prev.tierId,
        period: undefined,
        startDate: range?.from?.toISOString(),
        endDate: range?.to?.toISOString(),
      }),
    })
  }

  const hasActiveFilters = selectedProgram !== 'all' || selectedTier !== 'all' || hasCustomDates
  const handleClearFilters = () => navigate({ search: {} })

  const { data, isLoading, error } = useCreatorDashboard(filters)
  const { data: tiers } = useCommissionTiers()
  const { data: programs } = useCreatorPrograms()

  // Dynamic chart color based on trend (TradingView style)
  const salesTrend = useMemo(() => {
    const points = data?.charts.salesOverTime
    if (!points || points.length < 2) return 'up' as const
    const first = points[0].amount
    const last = points[points.length - 1].amount
    return last >= first ? ('up' as const) : ('down' as const)
  }, [data?.charts.salesOverTime])

  const salesChartColor = salesTrend === 'up' ? CHART_COLOR_UP : CHART_COLOR_DOWN

  const salesAreaConfig = useMemo(
    () => ({ amount: { label: 'Vendas', color: salesChartColor } }) satisfies ChartConfig,
    [salesChartColor]
  )

  // Pivot posts-over-time rows into { date, feed, reel, carousel } for stacked bars
  const postsChartData = useMemo(() => {
    const rows = data?.charts.postsOverTime ?? []
    const byDate: Record<string, Record<string, number>> = {}
    for (const row of rows) {
      if (!byDate[row.date]) byDate[row.date] = {}
      byDate[row.date][row.postType] = (byDate[row.date][row.postType] ?? 0) + row.count
    }
    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, types]) => ({ date, feed: 0, reel: 0, carousel: 0, ...types }))
  }, [data?.charts.postsOverTime])

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertCircle className="h-10 w-10 text-muted-foreground/50 mb-4" />
        <p className="text-base font-semibold">Erro ao carregar dashboard</p>
        <p className="text-sm text-muted-foreground mt-1">{error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Analytics de Creators"
        description="Performance e metricas do canal de creators."
        actions={
          <CreatorsAnalyticsFilters
            activePeriod={activePeriod}
            selectedTier={selectedTier}
            selectedProgram={selectedProgram}
            dateRange={dateRange}
            hasActiveFilters={hasActiveFilters}
            tiers={tiers}
            programs={programs}
            onPeriodChange={handlePeriodChange}
            onTierChange={handleTierChange}
            onProgramChange={handleProgramChange}
            onDateChange={handleDateChange}
            onClearFilters={handleClearFilters}
          />
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {isLoading
          ? KPI_CARDS.map((c) => <KpiCardSkeleton key={c.key} />)
          : KPI_CARDS.map((config) => (
              <KpiCard key={config.key} config={config} metric={data!.kpis[config.key]} />
            ))}
      </div>

      {/* Sales Over Time */}
      {isLoading ? (
        <ChartCardSkeleton />
      ) : (
        <SectionCard
          title="Vendas ao longo do tempo"
          description={`Total de vendas agrupado por ${activePeriod === '90d' || activePeriod === 'all' ? 'semana' : 'dia'}`}
        >
          {data!.charts.salesOverTime.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="rounded-full bg-muted p-3 mb-3">
                <ShoppingBag className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">Nenhuma venda no periodo</p>
            </div>
          ) : (
            <ChartContainer config={salesAreaConfig} className="aspect-auto h-70 w-full">
              <AreaChart
                data={data!.charts.salesOverTime}
                margin={{ left: -20, right: 12, top: 4 }}
              >
                <defs>
                  <linearGradient id="fillAmount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-amount)" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="var(--color-amount)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                  tickFormatter={(v: string) => {
                    const d = new Date(v + 'T00:00:00')
                    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
                  }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickCount={4}
                  tickFormatter={(v: number) => {
                    if (v >= 1000) return `${(v / 1000).toFixed(1)}k`
                    return v.toLocaleString('pt-BR')
                  }}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(v) => {
                        return new Date(v + 'T00:00:00').toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        })
                      }}
                      valueFormatter={(v) => fmtCurrency(v as number)}
                      indicator="dot"
                    />
                  }
                />
                <Area
                  type="linear"
                  dataKey="amount"
                  stroke="var(--color-amount)"
                  strokeWidth={2}
                  fill="url(#fillAmount)"
                />
              </AreaChart>
            </ChartContainer>
          )}
        </SectionCard>
      )}

      {/* Posts Over Time */}
      {isLoading ? (
        <ChartCardSkeleton />
      ) : (
        <SectionCard
          title="Posts ao longo do tempo"
          description={`Quantidade de posts por tipo, agrupado por ${activePeriod === '90d' || activePeriod === 'all' ? 'semana' : 'dia'}`}
        >
          {postsChartData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="rounded-full bg-muted p-3 mb-3">
                <Image className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">Nenhum post no periodo</p>
            </div>
          ) : (
            <ChartContainer config={postsChartConfig} className="aspect-auto h-70 w-full">
              <BarChart data={postsChartData} margin={{ left: -20, right: 12, top: 4 }}>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                  tickFormatter={(v: string) => {
                    const d = new Date(v + 'T00:00:00')
                    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
                  }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickCount={4}
                  allowDecimals={false}
                />
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      labelFormatter={(v) =>
                        new Date(v + 'T00:00:00').toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        })
                      }
                      indicator="dot"
                    />
                  }
                />
                <Legend
                  formatter={(value) => POST_TYPE_LABELS[value] ?? value}
                  iconType="square"
                  iconSize={8}
                />
                {Object.keys(POST_TYPE_CHART_COLORS).map((postType) => (
                  <Bar
                    key={postType}
                    dataKey={postType}
                    stackId="posts"
                    fill={POST_TYPE_CHART_COLORS[postType]}
                  />
                ))}
              </BarChart>
            </ChartContainer>
          )}
        </SectionCard>
      )}

      {/* Distribution + Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Distributions */}
        <div className="space-y-6">
          {isLoading ? (
            <>
              <ChartCardSkeleton />
              <ChartCardSkeleton />
              <ChartCardSkeleton />
            </>
          ) : (
            <>
              <SectionCard title="Creators por Status">
                <DistributionList
                  data={data!.charts.creatorsByStatus.map((d) => ({
                    key: d.status,
                    count: d.count,
                  }))}
                  labels={STATUS_LABELS}
                  colors={STATUS_COLORS}
                />
              </SectionCard>

              <SectionCard title="Creators por Tier">
                <DistributionList
                  data={data!.charts.creatorsByTier.map((d) => ({
                    key: d.tierName,
                    count: d.count,
                  }))}
                  labels={Object.fromEntries(
                    data!.charts.creatorsByTier.map((d) => [d.tierName, d.tierName])
                  )}
                  colors={Object.fromEntries(
                    data!.charts.creatorsByTier.map((d, i) => [
                      d.tierName,
                      [
                        'bg-primary',
                        'bg-amber-500',
                        'bg-blue-500',
                        'bg-purple-500',
                        'bg-destructive',
                      ][i % 5],
                    ])
                  )}
                />
              </SectionCard>

              <SectionCard title="Tipo de Contrato">
                <DistributionList
                  data={data!.charts.creatorsByContractType.map((d) => ({
                    key: d.contractType,
                    count: d.count,
                  }))}
                  labels={CONTRACT_LABELS}
                  colors={CONTRACT_COLORS}
                />
              </SectionCard>
            </>
          )}
        </div>

        {/* Right columns: Rankings */}
        <div className="lg:col-span-2 space-y-6">
          {isLoading ? (
            <>
              <ChartCardSkeleton />
              <ChartCardSkeleton />
            </>
          ) : (
            <>
              <SectionCard title="Top 10 Creators por Vendas">
                <RankingList
                  items={data!.charts.topCreatorsBySales.map((d) => ({
                    id: d.id,
                    name: d.name,
                    subtitle: d.instagramUsername ? `@${d.instagramUsername}` : null,
                    value: Number(d.amount) || 0,
                    href: `/creators/${d.id}?tab=overview`,
                  }))}
                  formatValue={fmtCurrency}
                />
              </SectionCard>

              <SectionCard title="Top 10 Creators por Engajamento">
                <RankingList
                  items={data!.charts.topCreatorsByEngagement.map((d) => ({
                    id: d.id,
                    name: d.name,
                    subtitle: d.instagramUsername ? `@${d.instagramUsername}` : null,
                    value: Number(d.engagement) || 0,
                    href: `/creators/${d.id}?tab=overview`,
                  }))}
                  formatValue={fmtCompact}
                />
              </SectionCard>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
